import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Folder, FolderPlus, Home } from "lucide-react";
import { LIBRARY_MAX_DEPTH } from "@kaba/shared";
import { api } from "../../lib/api";
import {
  refreshLibrary,
  scopeBase,
  useLibraryFolders,
  type FolderNode,
  type LibraryScope,
} from "../../lib/library";
import { toast } from "../../lib/toast";
import { Button, Sheet } from "../ui/ui";
import { PromptDialog } from "../ui/Prompt";

/**
 * Ordner der Bibliothek wählen (Verschieben, „In Bibliothek ablegen“). Navigation per Pfad statt
 * Baum – funktioniert am Handy genauso. `exclude` blendet einen Ordner samt Unterordnern aus
 * (ein Ordner kann nicht in sich selbst verschoben werden).
 */
export function FolderPicker({
  open,
  scope,
  title,
  confirm,
  start,
  exclude,
  targets,
  onClose,
  onPick,
}: {
  open: boolean;
  /** Board- oder Team-Bibliothek */
  scope: LibraryScope;
  title: string;
  confirm: string;
  start?: string | null;
  exclude?: string;
  /** 0.58: Wahl zwischen mehreren Bibliotheken (z. B. Board und Team); die erste ist `scope` */
  targets?: { label: string; scope: LibraryScope }[];
  onClose: () => void;
  onPick: (folderId: string | null, scope: LibraryScope) => void;
}) {
  const [target, setTarget] = useState(0);
  const active = targets?.[target]?.scope ?? scope;
  const { data: all = [] } = useLibraryFolders(active, open);
  const [cur, setCur] = useState<string | null>(start ?? null);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (open) {
      setCur(start ?? null);
      setTarget(0);
    }
  }, [open, start]);
  const byId = useMemo(() => new Map(all.map((f) => [f.id, f])), [all]);
  const path = useMemo(() => {
    const out: FolderNode[] = [];
    for (
      let f = cur ? byId.get(cur) : undefined, i = 0;
      f && i < 50;
      f = f.parentId ? byId.get(f.parentId) : undefined, i++
    )
      out.unshift(f);
    return out;
  }, [cur, byId]);
  const kids = all
    .filter((f) => f.parentId === cur && f.id !== exclude)
    .sort((a, b) => a.name.localeCompare(b.name, "de"));
  const here =
    path.at(-1)?.name ??
    (targets && targets.length > 1
      ? `${targets[target]?.label ?? "Bibliothek"} (oberste Ebene)`
      : "Bibliothek (oberste Ebene)");

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(o) => !o && onClose()}
        title={title}
        side="center"
      >
        <div className="flex flex-col gap-3 p-4">
          {targets && targets.length > 1 && (
            <div
              role="group"
              aria-label="Bibliothek"
              className="flex gap-1 self-start rounded-lg bg-black/5 p-0.5 dark:bg-white/10"
            >
              {targets.map((t, i) => (
                <button
                  key={t.label}
                  type="button"
                  aria-pressed={target === i}
                  onClick={() => {
                    setTarget(i);
                    setCur(null);
                  }}
                  className={
                    target === i
                      ? "min-h-11 rounded-md bg-[var(--panel)] px-4 text-sm font-semibold shadow-sm sm:min-h-9"
                      : "min-h-11 rounded-md px-4 text-sm text-[var(--ink-soft)] sm:min-h-9"
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <nav
            aria-label="Ordnerpfad"
            className="flex flex-wrap items-center gap-1 text-sm"
          >
            <button
              type="button"
              onClick={() => setCur(null)}
              className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 hover:bg-black/5 sm:min-h-9 dark:hover:bg-white/10"
              aria-current={!cur ? "location" : undefined}
            >
              <Home size={15} aria-hidden /> Bibliothek
            </button>
            {path.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1">
                <ChevronRight
                  size={14}
                  aria-hidden
                  className="text-[var(--ink-soft)]"
                />
                <button
                  type="button"
                  onClick={() => setCur(p.id)}
                  className="min-h-11 rounded-lg px-2 hover:bg-black/5 sm:min-h-9 dark:hover:bg-white/10"
                  aria-current={p.id === cur ? "location" : undefined}
                >
                  {p.name}
                </button>
              </span>
            ))}
          </nav>
          <ul
            className="flex max-h-72 flex-col overflow-y-auto rounded-xl border border-[var(--line)]"
            aria-label="Unterordner"
          >
            {kids.map((f) => (
              <li
                key={f.id}
                className="border-b border-[var(--line)] last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() => setCur(f.id)}
                  className="flex min-h-11 w-full items-center gap-2 px-3 text-left text-sm hover:bg-black/[0.03] dark:hover:bg-white/5"
                >
                  <Folder
                    size={16}
                    aria-hidden
                    className="text-[var(--ink-soft)]"
                  />
                  <span className="flex-1 truncate">{f.name}</span>
                  <ChevronRight
                    size={15}
                    aria-hidden
                    className="text-[var(--ink-soft)]"
                  />
                </button>
              </li>
            ))}
            {!kids.length && (
              <li className="px-3 py-3 text-sm text-[var(--ink-soft)]">
                Keine Unterordner.
              </li>
            )}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={() => onPick(cur, active)}>
              {confirm}
            </Button>
            <span className="min-w-0 flex-1 truncate text-sm text-[var(--ink-soft)]">
              Ziel: {here}
            </span>
            {path.length < LIBRARY_MAX_DEPTH && (
              <Button variant="ghost" onClick={() => setCreating(true)}>
                <FolderPlus size={16} aria-hidden /> Neuer Ordner
              </Button>
            )}
          </div>
        </div>
      </Sheet>
      <PromptDialog
        open={creating}
        title="Neuer Ordner"
        label="Name"
        confirm="Anlegen"
        onClose={() => setCreating(false)}
        onSubmit={async (name) => {
          setCreating(false);
          if (!name.trim()) return;
          try {
            const r = await api.post<{ id: string }>(
              `${scopeBase(active)}/folders`,
              { name: name.trim(), parentId: cur },
            );
            await refreshLibrary(active);
            setCur(r.id);
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
      />
    </>
  );
}
